import { useRef, useEffect, useLayoutEffect } from "react";
import { Send, Square, Lightbulb } from "lucide-react";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { CollabMessage } from "../../../models/audit/collabMessage";
import { CollabContextDocument } from "../../../models/audit/collabContextDocument";
import { FieldDiff } from "../../../models/audit/collabFieldDiff";
import { AgentEvent } from "../../../api/sseClient";
import { CollabStreamingMessage } from "./CollabStreamingMessage";
import { ContextDocumentsSection } from "./ContextDocumentsSection";
import { RevisionDiff } from "./RevisionDiff";

interface CollabChatPanelProps {
  messages: CollabMessage[];
  streamingContent: string;
  isStreaming: boolean;
  agentEvents?: AgentEvent[];
  heartbeatCounter?: number;
  isStalled?: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onCancel: () => void;
  disabled?: boolean;
  placeholder?: string;
  /** Per-assistant-message free-text diffs, keyed by message id. Rendered as
   *  changed-field chips under the message that made the change. */
  revisionDiffs?: Record<string, FieldDiff[]>;
  /** Reveal the given field in the plan panel (chip click). */
  onNavigateToField?: (fieldKey: string) => void;
  /** Send the chosen option as the answer to a clarification question. */
  onAnswerClarification?: (option: string) => void;
  /**
   * Grounding/context documents for the session. When `onAddContextDocuments`
   * is provided, a bordered "Context Documents" section renders above the input
   * with an upload button and per-document removal.
   */
  contextDocuments?: CollabContextDocument[];
  onAddContextDocuments?: (files: File[]) => void;
  onRemoveContextDocument?: (documentId: string) => void;
  contextDocumentsUploading?: boolean;
}

export const CollabChatPanel = ({
  messages,
  streamingContent,
  isStreaming,
  agentEvents,
  heartbeatCounter,
  isStalled,
  inputValue,
  onInputChange,
  onSend,
  onCancel,
  disabled = false,
  placeholder = "Type your feedback or questions...",
  revisionDiffs,
  onNavigateToField,
  onAnswerClarification,
  contextDocuments,
  onAddContextDocuments,
  onRemoveContextDocument,
  contextDocumentsUploading = false,
}: CollabChatPanelProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Whether the transcript is pinned to the bottom. Tracked from real scroll events —
  // which content appends never fire — so a tall message landing can't be misread as the
  // user having scrolled up. Starts true: a fresh conversation opens at the bottom.
  const isAtBottomRef = useRef(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Grow the input with its content (one line up to max-h, then scroll).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [inputValue]);

  // True while a scroll WE initiated with behavior:"smooth" is still animating.
  // Its intermediate scroll events must not unpin the transcript — without this
  // guard a smooth animation reads as "the user scrolled up" and following stops.
  const smoothScrollInFlightRef = useRef(false);

  const scrollToBottom = (behavior: ScrollBehavior) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (behavior === "smooth") {
      smoothScrollInFlightRef.current = true;
    }
    container.scrollTo({ top: container.scrollHeight, behavior });
  };

  // Any direct interaction with the transcript is the user taking over: the browser
  // cancels an in-flight smooth animation on wheel/touch, and a cancelled animation
  // never fires the at-bottom scroll event that would clear the flag — left set, it
  // would force-pin the user's own scrolling and yank them back on the next append.
  const handleUserScrollIntent = () => {
    smoothScrollInFlightRef.current = false;
  };

  // Record whether the user is at the bottom on every real scroll. Appending a message
  // grows scrollHeight but fires no scroll event, so this keeps the pre-append answer — the
  // effects below must not recompute it from a DOM that has already grown, or any message
  // taller than the threshold reads as "the user scrolled up". Events from our own smooth
  // animation keep the pin (we are heading to the bottom by intent); arrival clears the
  // in-flight flag and hands control back to real user scrolls.
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const atBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      160;
    if (smoothScrollInFlightRef.current) {
      if (atBottom) {
        smoothScrollInFlightRef.current = false;
      }
      isAtBottomRef.current = true;
      return;
    }
    isAtBottomRef.current = atBottom;
  };

  // Discrete conversation beats — a message landing, the thinking indicator appearing or
  // clearing — glide to the bottom, but only while pinned. Someone reading history
  // (scrolled up) is left where they are.
  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom("smooth");
    }
  }, [messages, isStreaming]);

  // Rapid growth — streamed tokens, mid-turn tool-call lines — follows instantly: a smooth
  // animation restarted on every chunk lags further behind the content the faster it
  // arrives, which reads as jerky rubber-banding.
  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom("auto");
    }
  }, [streamingContent, agentEvents?.length]);

  // Sending always returns to the bottom, even from deep in the history — the user
  // just asked a question; the answer will arrive there.
  const handleSend = () => {
    isAtBottomRef.current = true;
    scrollToBottom("smooth");
    onSend();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !disabled && !isStreaming) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Messages area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        onWheel={handleUserScrollIntent}
        onTouchStart={handleUserScrollIntent}
        onPointerDown={handleUserScrollIntent}
        className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4"
      >
        {messages.length === 0 && !streamingContent && !isStreaming && (
          <div className="text-sm text-stone-500 text-center py-8">
            The AI will analyze your documents and create a draft plan.
          </div>
        )}

        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1;
          const actionable = isLastMessage && !disabled && !isStreaming;
          const clarificationOptions =
            message.role === "assistant" &&
            message.kind === "clarification" &&
            message.clarification?.options?.length
              ? message.clarification.options
              : null;
          const isProposal =
            message.role === "assistant" && message.kind === "proposal";
          return (
            <div
              key={message.id}
              className={`flex animate-in fade-in slide-in-from-bottom-2 duration-200 ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-4 py-3 ${
                  message.role === "user"
                    ? "bg-blue-500 text-white"
                    : "bg-stone-100 text-stone-900"
                }`}
              >
                {isProposal && (
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-stone-400">
                    <Lightbulb className="h-3 w-3" />
                    Proposed changes
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap break-words">
                  {message.content}
                </div>
                {isProposal && actionable && (
                  <div className="mt-2 text-[11px] text-stone-400">
                    Reply to approve, adjust, or ask about these.
                  </div>
                )}
                {clarificationOptions && (
                  <div className="mt-2 flex flex-col gap-1.5">
                    {clarificationOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => onAnswerClarification?.(option)}
                        // Only the open question is answerable; resolved ones
                        // stay visible as a record of what was asked.
                        disabled={
                          disabled ||
                          isStreaming ||
                          !isLastMessage ||
                          !onAnswerClarification
                        }
                        className="rounded-md border border-stone-300 bg-white px-2.5 py-1.5 text-left text-xs leading-relaxed text-stone-700 transition-colors hover:border-stone-400 hover:bg-stone-50 disabled:pointer-events-none disabled:opacity-50"
                      >
                        {option}
                      </button>
                    ))}
                    {actionable && (
                      <button
                        type="button"
                        onClick={() => inputRef.current?.focus()}
                        className="self-start text-[11px] text-stone-400 underline-offset-2 hover:text-stone-600 hover:underline"
                      >
                        None of these? Chat about it instead
                      </button>
                    )}
                  </div>
                )}
                {message.role === "assistant" &&
                message.kind === "caveat" &&
                message.unapplied?.length ? (
                  <div className="mt-2 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-2">
                    <div className="text-xs font-semibold text-amber-700">
                      Not applied
                    </div>
                    {message.unapplied.map((item) => (
                      <div key={item.request} className="text-xs">
                        <span className="font-medium text-stone-700">
                          {item.request}
                        </span>
                        <span className="block text-stone-600">
                          {item.reason}
                        </span>
                        {item.suggestion && (
                          <span className="block italic text-stone-500">
                            {item.suggestion}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
                {message.role === "assistant" &&
                revisionDiffs?.[message.id]?.length ? (
                  <RevisionDiff
                    diffs={revisionDiffs[message.id]}
                    onNavigate={onNavigateToField}
                  />
                ) : null}
                <div
                  className={`text-xs mt-1 ${
                    message.role === "user" ? "text-blue-100" : "text-stone-500"
                  }`}
                >
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Streaming message */}
        <CollabStreamingMessage
          content={streamingContent}
          isStreaming={isStreaming}
          agentEvents={agentEvents}
          heartbeatCounter={heartbeatCounter}
          isStalled={isStalled}
        />
      </div>

      {/* Context Documents section (revise mode) */}
      {onAddContextDocuments && (
        <ContextDocumentsSection
          documents={contextDocuments}
          onAdd={onAddContextDocuments}
          onRemove={onRemoveContextDocument}
          uploading={contextDocumentsUploading}
        />
      )}

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-stone-200 p-4 bg-white">
        <div className="flex gap-2">
          <Textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="resize-none min-h-0 max-h-40 overflow-y-auto"
            rows={1}
            disabled={disabled || isStreaming}
          />
          <div className="flex flex-col gap-2">
            {isStreaming ? (
              <Button
                onClick={onCancel}
                variant="outline"
                size="icon"
                title="Stop"
              >
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || disabled}
                size="icon"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="text-xs text-stone-500 mt-2">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};
